import React, { useState, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { Alert, Button, Snackbar, CircularProgress } from '@mui/material';
import { MessageCircle, Share2 } from 'lucide-react';

// Import des composants
import AuthForm from './AuthForm';
import { ManageCompetence } from './components/ManageCompetence';
import Recommandations from './components/Recommendations';
import CompetencesList from './components/CompetencesList';
import ShareCompetence from './components/ShareCompetence';
import AladinnNavigation from './components/AladinnNavigation';
import Cours from './components/Cours';
import Quiz from './components/Quiz';
import TrainingWithGPT from './components/TrainingWithGPT';
import UploadDocument from './components/UploadDocument';
import ChatWithGPT from './components/ChatWithGPT';
import Dashboard from './components/Dashboard';
import InspirationSpace from './CP-Search/inspirationSpace';

function App() {
  const [user, setUser] = useState(null);
  const [currentCompetence, setCurrentCompetence] = useState(null);
  const [currentSection, setCurrentSection] = useState('competences');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Gestion des notifications
  const handleSnackbarOpen = (severity, message) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Gestion de l'authentification
  const handleAuthSuccess = () => {
    handleSnackbarOpen('success', 'Connexion réussie');
    setCurrentSection('competences');
    navigate('/');
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setCurrentCompetence(null);
      setCurrentSection('competences');
      handleSnackbarOpen('success', 'Vous avez été déconnecté avec succès');
      navigate('/');
    } catch (error) {
      handleSnackbarOpen('error', 'Erreur lors de la déconnexion');
    }
  };

  // Gestion des compétences avec ManageCompetence
  const {
    currentCompetenceProgress,
    level,
    error,
    loading,
    isEditingTitle,
    generatedMessage,
    customMessage,
    setCustomMessage,
    handleCompetenceUpdate,
    handleProgressUpdate,
    handleDeleteCompetence,
    handleRegenerateCourse,
    handleEditTitle,
    handleSelectCompetence,
  } = ManageCompetence(user, handleSnackbarOpen, currentCompetence, setCurrentCompetence);

  function ScrollToTop() {
    const { pathname } = useLocation();
  
    useEffect(() => {
      window.scrollTo(0, 0);
    }, [pathname]);
  
    return null;
  }
  
  // Surveillance de l'authentification
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        setUser(null);
        setCurrentCompetence(null);
        setCurrentSection('competences');
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Mise à jour de la compétence depuis la route
  useEffect(() => {
    if (location.state?.competence) {
      setCurrentCompetence(location.state.competence);
      setCurrentSection('details');
    }
  }, [location.state]);

  // Gestion du partage
  const handleShareCompetence = () => {
    if (!currentCompetence) {
      handleSnackbarOpen('error', 'Aucune compétence sélectionnée');
      return;
    }
    setShareDialogOpen(true);
  };

  // Gestion de la navigation
  const handleNavigation = (sectionId) => {
    setCurrentSection(sectionId);
    
    switch (sectionId) {
      case 'competences':
        navigate('/');
        break;
      case 'details':
        if (currentCompetence) {
          navigate(`/Recommandations/${currentCompetence.id}`, { 
            state: { competence: currentCompetence, userId: user?.uid }
          });
        } else {
          navigate('/');
        }
        break;
      case 'cours':
        navigate(currentCompetence ? `/cours/${currentCompetence.id}` : '/');
        break;
      case 'quiz':
        navigate(currentCompetence ? `/quiz/${currentCompetence.id}` : '/');
        break;
      case 'training':
        navigate(currentCompetence ? `/training/${currentCompetence.id}` : '/');
        break;
      case 'uploadDocument':
        navigate(currentCompetence ? `/uploadDocument/${currentCompetence.id}` : '/');
        break;
      case 'chat':
        navigate('/chat', {
          state: { competence: currentCompetence, userId: user?.uid }
        });
        break;
      case 'progression':
        navigate('/progression', {
          state: { userId: user?.uid }
        });
        break;
      case 'competences-acquises':
        navigate('/competences-acquises', {
          state: { userId: user?.uid }
        });
        break;
      case 'recherche':
        navigate(currentCompetence ? `/inspiration/${currentCompetence.id}` : '/');
        break;
      case 'conseils':
        navigate('/conseils', {
          state: { userId: user?.uid }
        });
        break;
      case 'dashboard':
        navigate('/dashboard', {
          state: { userId: user?.uid }
        });
        break;
      case 'partage':
        setShareDialogOpen(true);
        break;
      default:
        navigate(`/${sectionId}`);
    }
  };

  const competenceDisplayProps = {
    competence: currentCompetence,
    currentCompetenceProgress,
    level,
    isEditingTitle,
    userId: user?.uid,
    onEditTitle: handleEditTitle,
    onUpdate: handleCompetenceUpdate,
    onDelete: handleDeleteCompetence,
    onRegenerate: handleRegenerateCourse,
    onProgressUpdate: handleProgressUpdate,
  };

  // Rendu conditionnel en fonction de l'état de l'authentification et du chargement
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <AuthForm onAuthSuccess={handleAuthSuccess} setUser={setUser} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <CircularProgress />
        <span className="ml-2">Chargement...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <span className="text-red-600">{error}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AladinnNavigation
        user={user}
        onNavigate={handleNavigation}
        currentSection={currentSection}
        currentCompetence={currentCompetence}
        onLogout={handleLogout}
      />

      <main className="transition-all duration-300 lg:ml-64">
        {/* Barre de communication */}
        <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex justify-end items-center space-x-4">
              <Button
                variant="outlined"
                size="small"
                startIcon={<MessageCircle className="w-5 h-5" />}
                onClick={() => handleNavigation('chat')}
                className="text-purple-600 border-purple-600 hover:bg-purple-50"
              >
                Chat avec Aladinn
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Share2 className="w-5 h-5" />}
                onClick={handleShareCompetence}
                className="text-orange-600 border-orange-600 hover:bg-orange-50"
              >
                Partager
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-6">
          <Routes>
            <Route path="/" element={
              <CompetencesList
                userId={user.uid}
                onSelectCompetence={(competence) => {
                  handleSelectCompetence(competence);
                  setCurrentSection('details');
                  navigate(`/Recommandations/${competence.id}`, { 
                    state: { competence, userId: user.uid }
                  });
                }}
              />
            } />
            <Route path="/Recommandations/:competenceId" element={<Recommandations {...competenceDisplayProps} />} />
            <Route path="/inspiration/:competenceId" element={<InspirationSpace />} />
            <Route path="/cours/:competenceId?" element={<Cours competence={currentCompetence} userId={user.uid} />} />
            <Route path="/quiz/:competenceId?" element={<Quiz competence={currentCompetence} onQuizComplete={handleProgressUpdate} userId={user.uid} />} />
            <Route path="/training/:competenceId?" element={<TrainingWithGPT competence={currentCompetence} onExerciseComplete={handleProgressUpdate} userId={user.uid} />} />
            <Route path="/uploadDocument/:competenceId?" element={<UploadDocument competence={currentCompetence} userId={user.uid} />} />
            <Route path="/chat" element={<ChatWithGPT competence={currentCompetence} userId={user.uid} />} />
            <Route path="/progression" element={<Dashboard userId={user.uid} activeTab="progression" />} />
          </Routes>
        </div>
      </main>

      <ShareCompetence
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        competence={currentCompetence}
        user={user}
        generatedMessage={generatedMessage}
        customMessage={customMessage}
        setCustomMessage={setCustomMessage}
        onShare={(recipientEmail, subject, message) => {
          console.log('Sharing...', { recipientEmail, subject, message });
          handleSnackbarOpen('success', 'Invitation envoyée avec succès');
          setShareDialogOpen(false);
        }}
      />

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default App;